/**
 * Cache accounting.
 *
 * ⛔ WHY THIS FILE EXISTS AT ALL. spec.md §7.4: *"Instrument the cache from the first commit. The
 * existing AI call log has 17 columns and not one cache field; ship that shape again and the hit
 * rate is unmeasurable rather than merely bad."* Caching is the difference between $0.41 and several
 * dollars a pull request, and the only way to know whether the layout in §7.4 is working is to
 * record what every single call did.
 *
 * ⛔ THE THIRD OUTCOME IS THE ONE THAT MATTERS. Below the minimum cacheable prefix — 512 tokens on
 * Opus 5, 1,024 on Sonnet 5 — nothing is cached and **no error is returned**. A two-state
 * hit/miss counter records that as a miss forever and never explains why the hit rate is zero. It is
 * detected exactly as the API documents: `cache_creation == 0 && cache_read == 0`.
 */

import type { Backend as BackendKind } from "../config/schema.js"

export type CacheOutcome =
  /** Some part of the prefix was served from cache. */
  | "hit"
  /** Nothing was read, but a prefix was written for the next call to read. */
  | "miss"
  /**
   * Neither read nor written. ⚠️ Almost always a prefix below the provider minimum, which fails
   * SILENTLY. Never fold this into "miss" — a miss is a cache that is working and cold.
   */
  | "not_cached"

export interface CacheStats {
  /** Tokens written to the cache by this call. Charged at 1.25× (5m) or 2× (1h) base input. */
  readonly createdTokens: number
  /** Tokens served from cache. Charged at 0.1×, and ⭐ do not count toward the ITPM rate limit. */
  readonly readTokens: number
  /** Tokens that were neither read nor written — charged at full rate. */
  readonly uncachedInputTokens: number
  /** Split by TTL, because a 1h write costs 2× and only pays back after two reads. */
  readonly created1hTokens: number
  readonly created5mTokens: number
  readonly outcome: CacheOutcome
}

export const NO_CACHE_ACTIVITY: CacheStats = {
  createdTokens: 0,
  readTokens: 0,
  uncachedInputTokens: 0,
  created1hTokens: 0,
  created5mTokens: 0,
  outcome: "not_cached",
}

/**
 * Classify one call from the token counts both backends report.
 *
 * ⚠️ Order matters: a call can read AND write in the same request (the 5m diff block is new while
 * the 1h corpus block is warm). That is a HIT — the expensive prefix was reused.
 */
export function classify(createdTokens: number, readTokens: number): CacheOutcome {
  if (readTokens > 0) return "hit"
  if (createdTokens > 0) return "miss"
  return "not_cached"
}

export function cacheStats(input: {
  createdTokens: number
  readTokens: number
  uncachedInputTokens: number
  created1hTokens?: number
  created5mTokens?: number
}): CacheStats {
  return {
    createdTokens: input.createdTokens,
    readTokens: input.readTokens,
    uncachedInputTokens: input.uncachedInputTokens,
    created1hTokens: input.created1hTokens ?? 0,
    created5mTokens: input.created5mTokens ?? 0,
    outcome: classify(input.createdTokens, input.readTokens),
  }
}

export interface CacheLedgerRow {
  /** ISO 8601. */
  readonly at: string
  readonly backend: BackendKind
  readonly model: string
  readonly dimensionId: string
  readonly stats: CacheStats
  readonly durationMs: number
  readonly costUsd?: number
}

export interface CacheSummary {
  readonly calls: number
  readonly hits: number
  readonly misses: number
  readonly notCached: number
  /**
   * Hits ÷ calls. ⚠️ Reported alongside `notCached` and never on its own: a run where every call was
   * below the minimum prefix has a hit rate of 0 and a cache that was never given a chance.
   */
  readonly hitRate: number
  readonly readTokens: number
  readonly createdTokens: number
  readonly uncachedInputTokens: number
  readonly created1hTokens: number
  readonly created5mTokens: number
}

/**
 * Every model call, with what the cache did. In memory for now; W7 gives it a table.
 *
 * ⭐ Deliberately not a counter. The per-call rows are what let you answer "which dimension is
 * missing, and did serialising the first call actually help" — an aggregate cannot.
 */
export class CacheLedger {
  private readonly entries: CacheLedgerRow[] = []

  record(row: CacheLedgerRow): void {
    this.entries.push(row)
  }

  get rows(): readonly CacheLedgerRow[] {
    return this.entries
  }

  summary(): CacheSummary {
    const sum = (f: (s: CacheStats) => number): number =>
      this.entries.reduce((n, e) => n + f(e.stats), 0)
    const count = (o: CacheOutcome): number => this.entries.filter((e) => e.stats.outcome === o).length
    const calls = this.entries.length
    const hits = count("hit")
    return {
      calls,
      hits,
      misses: count("miss"),
      notCached: count("not_cached"),
      hitRate: calls === 0 ? 0 : hits / calls,
      readTokens: sum((s) => s.readTokens),
      createdTokens: sum((s) => s.createdTokens),
      uncachedInputTokens: sum((s) => s.uncachedInputTokens),
      created1hTokens: sum((s) => s.created1hTokens),
      created5mTokens: sum((s) => s.created5mTokens),
    }
  }
}
